<script lang="ts">
import { compactMoney } from '@/utilities/Money';
import { PolicySource } from '@/types/PolicySource';
import { EmployeeInfo, ServicingRole, AllowedUserForServicingRole } from '@/types/v2/Policy';
import AppSelect from '@/components/v2/AppSelect.vue';
import AppModal from '@/components/v2/AppModal.vue';
import Combobox, { ComboboxOption } from '@/components/v2/Combobox.vue';
import UIInput from '@/components/ui/UIInput.vue';
import { Undo2 } from 'lucide-vue-next';
import { Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';

export default {
  name: 'EditPolicyModal',
  components: {
    AppSelect,
    AppModal,
    Combobox,
    UIInput,
  },
};

interface Field {
  label: string;
  formatter?: (val: string) => string;
}

const policyFieldLabels: Record<string, Field> = {
  aggregateLimitOverride: { label: 'Aggregate Limit', formatter: compactMoney },
  occurrenceLimitOverride: { label: 'Occurrence Limit', formatter: compactMoney },
  peFlag: { label: 'Primary / Excess' },
  attachmentOverride: { label: 'Attachment', formatter: compactMoney },
  quotaShareTotalOverride: { label: 'Total Layer Limit', formatter: compactMoney },
  retentionOverride: { label: 'Retention', formatter: compactMoney },
  brokerOverride: { label: 'Broker' },
  sideAOverride: { label: 'SideA' },
  runoffOverride: { label: 'Runoff' },
  underwriterOverride: { label: 'Underwriter' },
} as const;
</script>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed, watch } from 'vue';
import z from 'zod';
import isNumeric from 'validator/lib/isNumeric';
import { addCommas, removeCommas } from '@/utilities/Number';
import { policyOverrideService, referenceService, carrierService, policySourceService } from '@/services/api';
import { policyService } from '@/services/v2/PolicyService';
import userService, { User } from '@/services/UserService';
import { PolicyOverride, OriginalValues } from '@/types/PolicyOverrides';
import { DateTime } from 'luxon';
import { AuditLog } from '@/types/AuditLog';

// custom validator for numeric strings (empty string is valid)
const numericString = z
  .string()
  .trim()
  .refine((val) => val === '' || isNumeric(val), {
    message: 'Must be a number',
  })
  .default('');

const formSchema = z.object({
  peFlag: z.string().trim().default(''),
  attachmentPointAmount: numericString,
  attachmentApplicable: z.boolean().default(true),
  aggregateLimitAmount: numericString,
  aggregateLimitApplicable: z.boolean().default(true),
  occurrenceLimitAmount: numericString,
  occurrenceLimitApplicable: z.boolean().default(true),
  quotaShareTotal: numericString,
  retentionAmount: numericString,
  retentionApplicable: z.boolean().default(true),
  leadSyndicateId: z.string().trim().default(''),
  sideAValue: z.string().trim().default(''),
  broker: z.string().trim().default(''),
  runoff: z.boolean().default(false),
  carrierId: numericString,
  premiumTotal: numericString,
});

type FormSchemeType = z.infer<typeof formSchema>;

type ValidFormFields = {
  [K in keyof FormSchemeType]: boolean;
};

const defaultValidFormFields: ValidFormFields = {
  peFlag: true,
  attachmentPointAmount: true,
  attachmentApplicable: true,
  aggregateLimitAmount: true,
  aggregateLimitApplicable: true,
  occurrenceLimitAmount: true,
  occurrenceLimitApplicable: true,
  quotaShareTotal: true,
  retentionAmount: true,
  retentionApplicable: true,
  leadSyndicateId: true,
  sideAValue: true,
  broker: true,
  runoff: true,
  carrierId: true,
  premiumTotal: true,
};

interface SelectOption {
  label: string;
  value: string;
}

const props = defineProps<{
  policyId: number;
  policyNumber: string;
  policySourceId?: number;
  isPolicyLine?: boolean;
  policyLineId?: number;
  show: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'save'): void;
}>();

// ----------------
// refs and consts
// ----------------
const form = ref<FormSchemeType>(formSchema.parse({})); // zero values for all form fields
const originalForm = ref<FormSchemeType>(formSchema.parse({}));
const validFormFields = ref<ValidFormFields>(structuredClone(defaultValidFormFields));
const policySource = ref<PolicySource>();
const syndicateOptions = ref<SelectOption[]>([]);
const carrierOptions = ref<SelectOption[]>([]);
const sideAOptions = ref<string[]>(['None', 'Side A', 'Side A DIC', 'Side A IDL']);
const showHistory = ref(false);
const overrideHistory = ref<AuditLog[]>([]);
const loading = ref(false);
const saving = ref(false);
const activeTab = ref<'structure' | 'servicing'>('structure');
const servicingRoles = ref<ServicingRole[]>([]);
const originalServicingRoles = ref<ServicingRole[]>([]);
const servicingRolesLoading = ref(false);
const servicingRolesFetched = ref(false);
const allUsers = ref<User[]>([]);
const usersFetched = ref(false);
const changedServicingRoles = ref<Set<number>>(new Set());
const servicingRolesAuditHistory = ref<AuditLog[]>([]);
const allowedUsersForRoles = ref<AllowedUserForServicingRole[]>([]);
const allowedUsersFetched = ref(false);

watch(
  () => props.show,
  async (show) => {
    if (show) {
      cleanup();
      loading.value = true;

      try {
        await Promise.all([loadPolicyData(), loadHistory(), getPolicySource()]);
      } catch (error) {
        console.error('Error loading policy data:', error);
        // Reset form to defaults if loading fails
        form.value = formSchema.parse({});
        originalForm.value = formSchema.parse({});
        validFormFields.value = structuredClone(defaultValidFormFields);
      } finally {
        loading.value = false;
      }
    }
  },
);

watch(activeTab, async (newTab) => {
  if (newTab === 'servicing') {
    servicingRolesLoading.value = true;

    try {
      const promises: (Promise<ServicingRole[]> | Promise<User[]> | Promise<AllowedUserForServicingRole[]>)[] = [];

      if (!servicingRolesFetched.value) {
        promises.push(policyService.getPolicyLineServicingRoles(props.policyId));
      }

      if (!usersFetched.value) {
        promises.push(userService.getAllEmployeeUsers());
      }

      if (!allowedUsersFetched.value) {
        promises.push(policyService.getAllowedUsersForServicingRoles());
      }

      const results = await Promise.all(promises);

      let resultIndex = 0;

      if (!servicingRolesFetched.value) {
        servicingRoles.value = results[resultIndex] as ServicingRole[];
        originalServicingRoles.value = JSON.parse(JSON.stringify(results[resultIndex])) as ServicingRole[];
        servicingRolesFetched.value = true;
        resultIndex++;
      }

      if (!usersFetched.value) {
        allUsers.value = results[resultIndex] as User[];
        usersFetched.value = true;
        resultIndex++;
      }

      if (!allowedUsersFetched.value) {
        allowedUsersForRoles.value = results[resultIndex] as AllowedUserForServicingRole[];
        allowedUsersFetched.value = true;
      }
    } catch (error) {
      console.error('Error fetching servicing data:', error);
      if (!servicingRolesFetched.value) {
        servicingRoles.value = [];
      }
      if (!usersFetched.value) {
        allUsers.value = [];
      }
      if (!allowedUsersFetched.value) {
        allowedUsersForRoles.value = [];
      }
    } finally {
      servicingRolesLoading.value = false;
    }
  }
});

// Also watch showHistory to load data when viewing history
watch([() => props.show, showHistory], async ([show, history]) => {
  if (show && history && activeTab.value === 'servicing') {
    // Load servicing roles and users if not already loaded
    if (!servicingRolesFetched.value || !usersFetched.value || !allowedUsersFetched.value) {
      try {
        const promises: (Promise<ServicingRole[]> | Promise<User[]> | Promise<AllowedUserForServicingRole[]>)[] = [];

        if (!servicingRolesFetched.value) {
          promises.push(policyService.getPolicyLineServicingRoles(props.policyId));
        }

        if (!usersFetched.value) {
          promises.push(userService.getAllEmployeeUsers());
        }

        if (!allowedUsersFetched.value) {
          promises.push(policyService.getAllowedUsersForServicingRoles());
        }

        const results = await Promise.all(promises);

        let resultIndex = 0;

        if (!servicingRolesFetched.value) {
          servicingRoles.value = results[resultIndex] as ServicingRole[];
          originalServicingRoles.value = JSON.parse(JSON.stringify(results[resultIndex])) as ServicingRole[];
          servicingRolesFetched.value = true;
          resultIndex++;
        }

        if (!usersFetched.value) {
          allUsers.value = results[resultIndex] as User[];
          usersFetched.value = true;
          resultIndex++;
        }

        if (!allowedUsersFetched.value) {
          allowedUsersForRoles.value = results[resultIndex] as AllowedUserForServicingRole[];
          allowedUsersFetched.value = true;
        }
      } catch (error) {
        console.error('Error fetching servicing data for history:', error);
      }
    }
  }
});

// ---------
// computed
// ---------
const attachmentPointAmount = computed({
  get: () => {
    return form.value.attachmentApplicable ? addCommas(form.value.attachmentPointAmount ?? '') : '';
  },
  set: (val) => {
    form.value.attachmentPointAmount = removeCommas(val)?.toString() || '';
  },
});

const aggregateLimitAmount = computed({
  get: () => {
    return form.value.aggregateLimitApplicable ? addCommas(form.value.aggregateLimitAmount ?? '') : '';
  },
  set: (val) => {
    form.value.aggregateLimitAmount = removeCommas(val)?.toString() ?? '';
  },
});

const occurrenceLimitAmount = computed({
  get: () => {
    return form.value.occurrenceLimitApplicable ? addCommas(form.value.occurrenceLimitAmount ?? '') : '';
  },
  set: (val) => {
    form.value.occurrenceLimitAmount = removeCommas(val)?.toString() ?? '';
  },
});

const quotaShareTotal = computed({
  get: () => {
    return addCommas(form.value.quotaShareTotal ?? '');
  },
  set: (val) => {
    form.value.quotaShareTotal = removeCommas(val)?.toString() ?? '';
  },
});

const retentionAmount = computed({
  get: () => {
    return form.value.retentionApplicable ? addCommas(form.value.retentionAmount ?? '') : '';
  },
  set: (val) => {
    form.value.retentionAmount = removeCommas(val)?.toString() ?? '';
  },
});

const premiumTotal = computed({
  get: () => {
    return addCommas(form.value.premiumTotal ?? '');
  },
  set: (val) => {
    form.value.premiumTotal = removeCommas(val)?.toString() ?? '';
  },
});

const isFormUpdated = computed(() => {
  const structureChanged = !isEqual(form.value, originalForm.value);
  const servicingChanged = changedServicingRoles.value.size > 0;
  return structureChanged || servicingChanged;
});

const hasStructureChanges = computed(() => {
  return !isEqual(form.value, originalForm.value);
});

const hasServicingChanges = computed(() => {
  return changedServicingRoles.value.size > 0;
});

// Track which structure fields have changed
const structureFieldsChanged = computed(() => {
  const changes: Record<string, boolean> = {};

  // Compare each field with its original value
  changes.peFlag = form.value.peFlag !== originalForm.value.peFlag;
  changes.attachmentPointAmount = form.value.attachmentPointAmount !== originalForm.value.attachmentPointAmount;
  changes.attachmentApplicable = form.value.attachmentApplicable !== originalForm.value.attachmentApplicable;
  changes.aggregateLimitAmount = form.value.aggregateLimitAmount !== originalForm.value.aggregateLimitAmount;
  changes.aggregateLimitApplicable =
    form.value.aggregateLimitApplicable !== originalForm.value.aggregateLimitApplicable;
  changes.occurrenceLimitAmount = form.value.occurrenceLimitAmount !== originalForm.value.occurrenceLimitAmount;
  changes.occurrenceLimitApplicable =
    form.value.occurrenceLimitApplicable !== originalForm.value.occurrenceLimitApplicable;
  changes.quotaShareTotal = form.value.quotaShareTotal !== originalForm.value.quotaShareTotal;
  changes.retentionAmount = form.value.retentionAmount !== originalForm.value.retentionAmount;
  changes.retentionApplicable = form.value.retentionApplicable !== originalForm.value.retentionApplicable;
  changes.leadSyndicateId = form.value.leadSyndicateId !== originalForm.value.leadSyndicateId;
  changes.sideAValue = form.value.sideAValue !== originalForm.value.sideAValue;
  changes.broker = form.value.broker !== originalForm.value.broker;
  changes.runoff = form.value.runoff !== originalForm.value.runoff;
  changes.carrierId = form.value.carrierId !== originalForm.value.carrierId;
  changes.premiumTotal = form.value.premiumTotal !== originalForm.value.premiumTotal;

  return changes;
});

/**
 * Determine whether the policy is an external policy by the provided policy source id.
 * Otherwise, it will be assumed to be an internal policy.
 */
const isExternalPolicy = computed(() => {
  return props.policySourceId && ![1, 4, 5].includes(props.policySourceId ?? 0);
});

const isPolicyLine = computed(() => {
  return (props.isPolicyLine && props.policyLineId) ?? false;
});

const isFormDisabled = computed(() => {
  return loading.value || saving.value;
});

const userOptions = computed<ComboboxOption[]>(() => {
  return allUsers.value.map((user) => ({
    label: user.name || user.userName || user.userEmail,
    value: user.sourceEmployeeKey,
  }));
});

// -----------
// lifecycles
// -----------
onMounted(async () => {
  addEventListener('keyup', handleCloseWithEscapeKey);

  const syndicates = await referenceService.syndicates();
  syndicateOptions.value = syndicates.map((s) => ({
    label: `${s.number}-${s.name}`,
    value: s.id.toString(),
  }));

  const carriers = await carrierService.list();
  carrierOptions.value = carriers.map((carrier) => {
    return {
      label: carrier.name,
      value: carrier.ID.toString(),
    };
  });
});

onUnmounted(() => {
  removeEventListener('keyup', handleCloseWithEscapeKey);
});

// ---------
// handlers
// ---------
function handleCloseWithEscapeKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    handleClose();
  }
}

function handleClose(): void {
  emit('close');
}

async function handleSave(): Promise<void> {
  try {
    // validate the form
    if (!validateForm()) {
      console.log('invalid form');
      return;
    }

    // save the form
    saving.value = true;

    // Save structure data if on structure tab or if there are structure changes
    if (activeTab.value === 'structure' || !isEqual(form.value, originalForm.value)) {
      if (props.isPolicyLine && props.policyLineId) {
        await policyOverrideService.saveLineOverride(
          props.policyId.toString(),
          props.policyLineId.toString(),
          toPolicyOverride(form.value),
        );
      } else {
        await policyOverrideService.saveOverride(props.policyId.toString(), toPolicyOverride(form.value));
      }
    }

    // Save servicing roles if on servicing tab and there are changes
    if (activeTab.value === 'servicing' && changedServicingRoles.value.size > 0) {
      const updatedRoles = servicingRoles.value
        .filter((_, index) => changedServicingRoles.value.has(index))
        .map((r, originalIndex) => {
          // Find the actual index in the original array
          const actualIndex = servicingRoles.value.findIndex(
            (role, idx) => changedServicingRoles.value.has(idx) && role.code === r.code,
          );

          // Check if the employee assignment has changed (not just published status)
          const originalEmployee = originalServicingRoles.value[actualIndex]?.employeeInfo?.lookupCode || '';
          const currentEmployee = r.employeeInfo.lookupCode;
          const epicChanges = originalEmployee !== currentEmployee;

          return {
            code: r.code,
            employeeLookupCode: r.employeeInfo.lookupCode,
            published: r.published ?? true,
            epicChanges: epicChanges,
          };
        });

      await policyService.updatePolicyLineServicingRoles(props.policyId, updatedRoles);
    }
  } catch (e) {
    console.error('policy edit save error', e);
    saving.value = false;
    Commit(StoreMutationType.SET_FLASH_MESSAGE, {
      text: 'Error saving policy changes',
      color: 'red',
    });
    return;
  }

  saving.value = false;
  emit('save');
}

// ------------------
// private functions
// ------------------

function validateForm(): boolean {
  const valid = formSchema.safeParse(form.value);
  if (!valid.success) {
    // reset all valid form fields to true
    validFormFields.value = structuredClone(defaultValidFormFields);

    // set valid form fields to false
    for (const error of valid.error.issues) {
      validFormFields.value[error.path[0].toString() as keyof ValidFormFields] = false;
    }
  }
  return valid.success;
}

function toForm(originals: Partial<OriginalValues>, overrides: Partial<PolicyOverride>): FormSchemeType {
  const peFlag = overrides.peFlag || originals.peFlag;
  const hasRetention = !!(overrides.retentionOverride || originals.retention);
  const hasAttachment = !!(overrides.attachmentOverride || originals.attachment);

  return {
    peFlag: peFlag,
    attachmentPointAmount: overrides.attachmentOverride?.toString() || originals.attachment?.toString() || '',
    attachmentApplicable:
      peFlag === 'Primary' && !hasAttachment
        ? false
        : overrides.noAttachment === undefined
          ? true
          : !overrides.noAttachment,
    aggregateLimitAmount: overrides.aggregateLimitOverride?.toString() || originals.aggregateLimit?.toString() || '',
    aggregateLimitApplicable: overrides.noLimit === undefined ? true : !overrides.noLimit,
    occurrenceLimitAmount: overrides.occurrenceLimitOverride?.toString() || originals.occurrenceLimit?.toString() || '',
    occurrenceLimitApplicable: overrides.noOccurrenceLimit === undefined ? true : !overrides.noOccurrenceLimit,
    quotaShareTotal: overrides.quotaShareTotalOverride?.toString() || originals.quotaShareTotal?.toString() || '',
    retentionAmount: overrides.retentionOverride?.toString() || originals.retention?.toString() || '',
    retentionApplicable:
      peFlag === 'Excess' && !hasRetention ? false : overrides.noRetention === undefined ? true : !overrides.noRetention,
    leadSyndicateId: overrides.syndicateId?.toString() || originals.syndicateId?.toString() || '',
    sideAValue: overrides.sideAOverride ?? originals.sideA,
    runoff: overrides.runoffOverride ?? originals.runoff === 'Runoff', // boolean in override table, string in original table ('Runoff' or '')
    carrierId: getCarrierId(overrides.carrierOverride ?? '') ?? getCarrierId(originals.carrier ?? ''),
    premiumTotal: overrides.policyPremiumOverride?.toString() || originals.policyPremium?.toString() || '',
  } as FormSchemeType;
}

function toPolicyOverride(form: FormSchemeType): PolicyOverride {
  const override = {
    peFlag: form.peFlag ?? '',
    attachmentOverride:
      form.attachmentPointAmount && form.attachmentApplicable ? Number(form.attachmentPointAmount) : undefined,
    noAttachment: !form.attachmentApplicable,
    aggregateLimitOverride:
      form.aggregateLimitAmount && form.aggregateLimitApplicable ? Number(form.aggregateLimitAmount) : undefined,
    noLimit: !form.aggregateLimitApplicable,
    occurrenceLimitOverride:
      form.occurrenceLimitAmount && form.occurrenceLimitApplicable ? Number(form.occurrenceLimitAmount) : undefined,
    noOccurrenceLimit: !form.occurrenceLimitApplicable,
    quotaShareTotalOverride: form.quotaShareTotal, // this should be a number, but for now it's a string. will have to fix this later
    retentionOverride: form.retentionAmount && form.retentionApplicable ? Number(form.retentionAmount) : undefined,
    noRetention: !form.retentionApplicable,
    syndicateId: form.leadSyndicateId ? Number(form.leadSyndicateId) : undefined,
    sideAOverride: form.sideAValue,
    runoffOverride: form.runoff,
  } as PolicyOverride;

  if (isExternalPolicy.value) {
    if (policySource.value?.AllowCarrierUpdate) {
      override.carrierOverride = getCarrierName(form.carrierId);
    }

    if (policySource.value?.AllowPremiumUpdate) {
      override.policyPremiumOverride = form.premiumTotal ? Number(form.premiumTotal) : undefined;
    }
  }

  if (isPolicyLine.value) {
    override.policyPremiumOverride = form.premiumTotal ? Number(form.premiumTotal) : undefined;
  }

  return override;
}

function isEqual(a: FormSchemeType, b: FormSchemeType): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

async function loadPolicy(policyId: number): Promise<void> {
  const overrideAndOriginalFields = await policyOverrideService.GetCurrentOverride(policyId.toString());
  const transform = toForm(overrideAndOriginalFields.originalValues, overrideAndOriginalFields.policyOverride);
  form.value = transform;
  originalForm.value = structuredClone(transform);
}

async function loadPolicyLine(policyId: number, policyLineId: number): Promise<void> {
  const overrideAndOriginalFields = await policyOverrideService.GetCurrentLineOverride(
    policyId.toString(),
    policyLineId.toString(),
  );
  const transform = toForm(overrideAndOriginalFields.originalValues, overrideAndOriginalFields.policyOverride);
  form.value = transform;
  originalForm.value = structuredClone(transform);
}

function formatDiffValue(key: string, val: string): string {
  return policyFieldLabels[key]?.formatter?.(val) ?? val;
}

function changeDiff(jsonStr: string): Record<string, { changed: string; original: string }> {
  return JSON.parse(jsonStr) as Record<string, { changed: string; original: string }>;
}

function cleanup(): void {
  form.value = formSchema.parse({}); // zero values for all form fields
  originalForm.value = formSchema.parse({});
  validFormFields.value = structuredClone(defaultValidFormFields);
  showHistory.value = false;
  overrideHistory.value = [];
  loading.value = false;
  activeTab.value = 'structure';
  servicingRoles.value = [];
  originalServicingRoles.value = [];
  servicingRolesFetched.value = false;
  servicingRolesLoading.value = false;
  allUsers.value = [];
  usersFetched.value = false;
  changedServicingRoles.value.clear();
  allowedUsersForRoles.value = [];
  allowedUsersFetched.value = false;
}

function getCarrierId(name: string): string {
  const carrierOption = carrierOptions.value.find((c) => c.label.trim() === name.trim());
  return carrierOption?.value ?? '';
}

function getCarrierName(id: string): string {
  const carrierOption = carrierOptions.value.find((c) => c.value === id);
  return carrierOption?.label ?? '';
}

async function loadPolicyData(): Promise<void> {
  if (props.isPolicyLine && props.policyLineId) {
    await loadPolicyLine(props.policyId, props.policyLineId);
  } else {
    await loadPolicy(props.policyId);
  }
}

async function loadHistory(): Promise<void> {
  if (props.isPolicyLine && props.policyLineId) {
    overrideHistory.value = await policyOverrideService.GetLineOverrideHistory(
      props.policyId.toString(),
      props.policyLineId.toString(),
    );
  } else {
    overrideHistory.value = await policyOverrideService.GetOverrideHistory(props.policyId);
  }

  servicingRolesAuditHistory.value = await policyService.getPolicyLineServicingRolesAuditHistory(props.policyId);
}

async function getPolicySource(): Promise<void> {
  if (props.policySourceId) {
    policySource.value = await policySourceService.getPolicySourceById(props.policySourceId);
  }
}

function isValidEmployee(employee: EmployeeInfo): boolean {
  // Empty keys should show combobox for selection
  if (!employee.lookupCode) {
    return true;
  }

  // Inactive employees should not be selectable
  if (employee.accountStatus === 'Inactive') {
    return false;
  }

  return userOptions.value.some((option) => option.value === employee.lookupCode);
}

function isNoLongerEmployed(employee: EmployeeInfo): boolean {
  return employee.accountStatus === 'Inactive';
}

function createStatusText(employee: EmployeeInfo): string {
  if (employee.accountStatus === 'Inactive') {
    return `${employee.firstName} ${employee.lastName} (${employee.inactiveReason})`; // Status for inactive employees
  }

  return "Employee key doesn't exist in the user list";
}

function handleServicingRoleChange(index: number, newValue: string): void {
  // Find the selected user to get their full employee info
  const selectedUser = allUsers.value.find((u) => u.sourceEmployeeKey === newValue);
  
  if (selectedUser && newValue) {
    // Update the entire employeeInfo object with the selected user's data
    servicingRoles.value[index].employeeInfo = {
      lookupCode: newValue,
      firstName: selectedUser.name?.split(' ')[0] || '',
      lastName: selectedUser.name?.split(' ').slice(1).join(' ') || '',
      accountStatus: 'Active',
      inactiveReason: '',
    };
  } else {
    // If no user selected (empty), just update the lookupCode
    servicingRoles.value[index].employeeInfo.lookupCode = newValue;
  }

  // Set published to true by default when a user is selected
  if (newValue) {
    servicingRoles.value[index].published = true;
  }

  // Check if the value is different from the original
  const originalValue = originalServicingRoles.value[index]?.employeeInfo?.lookupCode || '';
  if (newValue !== originalValue) {
    changedServicingRoles.value.add(index);
  } else {
    changedServicingRoles.value.delete(index);
  }

  // Force reactivity update
  changedServicingRoles.value = new Set(changedServicingRoles.value);
}

function handleClientViewableChange(index: number): void {
  // Check if either employee lookup or published has changed from original
  const originalLookupCode = originalServicingRoles.value[index]?.employeeInfo?.lookupCode || '';
  const currentLookupCode = servicingRoles.value[index].employeeInfo.lookupCode;
  const originalPublished = originalServicingRoles.value[index]?.published ?? true;
  const currentPublished = servicingRoles.value[index].published ?? true;

  if (originalLookupCode !== currentLookupCode || originalPublished !== currentPublished) {
    changedServicingRoles.value.add(index);
  } else {
    changedServicingRoles.value.delete(index);
  }

  // Force reactivity update
  changedServicingRoles.value = new Set(changedServicingRoles.value);
}

function revertServicingRole(index: number): void {
  const originalValue = originalServicingRoles.value[index]?.employeeInfo?.lookupCode || '';
  const originalPublished = originalServicingRoles.value[index]?.published ?? true;

  servicingRoles.value[index].employeeInfo.lookupCode = originalValue;
  servicingRoles.value[index].published = originalPublished;
  changedServicingRoles.value.delete(index);

  // Force reactivity update
  changedServicingRoles.value = new Set(changedServicingRoles.value);
}

function isServicingRoleChanged(index: number): boolean {
  return changedServicingRoles.value.has(index);
}

function parseServicingRolesChangeDiff(changeDiff: string): Record<string, { changed: string; original: string }> {
  try {
    // The changeDiff for servicing roles might be in a different format
    // Let's try to parse it as JSON first
    const parsed = JSON.parse(changeDiff);

    // If it's already in the correct format, return it
    if (typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed;
    }

    // Otherwise, we need to transform it
    // This is a placeholder - adjust based on actual data structure
    return {};
  } catch (e) {
    // If it's not JSON, return empty object
    console.error('Error parsing servicing roles change diff:', e);
    return {};
  }
}

function getServicingRoleByCode(code: string): ServicingRole | undefined {
  return servicingRoles.value.find((r) => r.code === code);
}

function formatServicingRoleFieldLabel(code: string): string {
  const role = getServicingRoleByCode(code);
  if (role) {
    // Format: description (code)
    if (role.description) {
      return `${role.description} (${code})`;
    } else if (role.role) {
      return `${role.role} (${code})`;
    }
  }
  return code;
}

function getUserNameByLookupCode(lookupCode: string): string {
  const user = allUsers.value.find((u) => u.sourceEmployeeKey === lookupCode);
  if (user) {
    return user.name || user.userName || user.userEmail;
  }
  return lookupCode || '--';
}

function isCurrentlySelectedEmployeeInactive(role: ServicingRole): boolean {
  if (!role.employeeInfo?.lookupCode) {
    return false;
  }
  
  return isNoLongerEmployed(role.employeeInfo);
}

function getAllowedUserIdsForRole(role: ServicingRole): number[] {
  if (allowedUsersForRoles.value.length === 0) {
    // If no allowed users data is loaded, return all user IDs (no filtering)
    return allUsers.value.map((u) => u.id);
  }

  // Match by servicingRoleCode
  return allowedUsersForRoles.value
    .filter((mapping) => mapping.servicingRoleCode === role.code)
    .map((mapping) => mapping.userId);
}

function getUserOptionsForRole(role: ServicingRole): ComboboxOption[] {
  const allowedUserIds = getAllowedUserIdsForRole(role);
  
  return allUsers.value
    .filter((user) => allowedUserIds.includes(user.id))
    .map((user) => ({
      label: user.name || user.userName || user.userEmail,
      value: user.sourceEmployeeKey,
    }));
}

// When the role is currently occupied by an inactive employee, show that employee in the combobox
function getUserOptionsForRoleWithInactive(role: ServicingRole): ComboboxOption[] {
  const options = getUserOptionsForRole(role);

  // If the current employee is inactive AND has a lookupCode, add them to the options
  if (isNoLongerEmployed(role.employeeInfo) && role.employeeInfo.lookupCode) {
    const alreadyIncluded = options.some((opt) => opt.value === role.employeeInfo.lookupCode);
    if (!alreadyIncluded) {
      options.push({
        label: `${role.employeeInfo.firstName} ${role.employeeInfo.lastName}`,
        value: role.employeeInfo.lookupCode,
      });
    }
  }

  return options;
}
</script>

<template>
  <AppModal
    :show="show"
    size="lg"
    :title="`Edit Policy${isPolicyLine ? ' Line' : ''}: ${policyNumber || policyId}`"
    :loading="loading || saving"
    modal-class="tw:max-h-[85vh]"
    :actionDisabled="!isFormUpdated || isFormDisabled"
    :handle-cancel="() => handleClose()"
    :handle-action="() => handleSave()">
    <template #title>
      <div class="tw:flex tw:justify-between">
        <h2 class="tw:self-center">Edit Policy{{ isPolicyLine ? ' Line' : '' }}: {{ policyNumber || policyId }}</h2>
        <button
          @click="!showHistory ? (showHistory = true) : (showHistory = false)"
          class="tw:btn tw:self-center tw:btn-xs tw:hover:bg-red-300"
          :class="{
            'tw:btn-disabled':
              activeTab === 'structure' ? overrideHistory.length === 0 : servicingRolesAuditHistory.length === 0,
          }">
          {{ showHistory ? 'Exit History' : 'Show History' }}
        </button>
      </div>
    </template>

    <template #content>
      <div class="tw:w-full tw:px-4" :class="{ 'tw:overflow-y-auto': showHistory }">
        <!-- Tab Navigation -->
        <div v-if="!showHistory" class="tw:mb-4 tw:tabs tw:w-fit tw:tabs-box tw:tabs-sm">
          <a
            class="tw:tab tw:flex tw:items-center tw:gap-2"
            :class="{ 'tw:tab-active': activeTab === 'structure' }"
            @click="activeTab = 'structure'">
            <span>Structure</span>
            <span v-if="hasStructureChanges" class="tw:h-2 tw:w-2 tw:rounded-full tw:bg-green-500"></span>
          </a>
          <a
            class="tw:tab tw:flex tw:items-center tw:gap-2"
            :class="{ 'tw:tab-active': activeTab === 'servicing' }"
            @click="activeTab = 'servicing'">
            <span>Servicing Roles</span>
            <span v-if="hasServicingChanges" class="tw:h-2 tw:w-2 tw:rounded-full tw:bg-green-500"></span>
          </a>
        </div>

        <!-- Tab Content with Transition -->
        <div v-if="!showHistory" class="tw:relative tw:overflow-visible">
          <transition name="slide-fade" mode="out-in">
            <div :key="activeTab" class="tw:flex tw:flex-col tw:gap-4">
              <!-- Structure Tab Content -->
              <template v-if="activeTab === 'structure'">
                <div class="tw:flex tw:gap-6">
                  <div class="tw:w-1/2" :class="{ 'tw:hidden': isPolicyLine }">
                    <label class="tw:label">
                      <span class="tw:label-text tw:text-xs tw:text-gray-500">Primary / Excess</span>
                    </label>
                    <AppSelect
                      v-model="form.peFlag"
                      :options="[
                        { label: 'Primary', value: 'Primary' },
                        { label: 'Excess', value: 'Excess' },
                        { label: 'Fees', value: 'Fees' },
                      ]"
                      :disabled="isFormDisabled"
                      :class="`tw:h-10 tw:w-full tw:rounded-sm ${structureFieldsChanged.peFlag ? 'tw:border-green-500' : ''}`" />
                  </div>
                  <div class="tw:w-1/2">
                    <label class="tw:label">
                      <span class="tw:label-text tw:text-xs tw:text-gray-500">Attachment Point</span>
                    </label>
                    <div class="tw:flex tw:flex-row tw:gap-2">
                      <input
                        type="text"
                        inputmode="numeric"
                        placeholder=""
                        v-model="attachmentPointAmount"
                        class="tw:input tw:input-md tw:w-full tw:border tw:border-solid"
                        :class="{
                          'tw:border-red-500': !validFormFields.attachmentPointAmount,
                          'tw:border-green-500':
                            structureFieldsChanged.attachmentPointAmount && validFormFields.attachmentPointAmount,
                        }"
                        :disabled="!form.attachmentApplicable || isFormDisabled" />
                      <label class="tw:form-control tw:label">
                        <input
                          type="checkbox"
                          class="tw:toggle tw:border tw:border-solid tw:border-gray-400 tw:toggle-primary"
                          :class="{ 'tw:border-green-500': structureFieldsChanged.attachmentApplicable }"
                          v-model="form.attachmentApplicable"
                          :disabled="isFormDisabled"
                          :checked="form.attachmentApplicable" />
                        <span
                          class="tw:text-center tw:align-top tw:text-xs"
                          :class="form.attachmentApplicable && 'tw:text-primary'"
                          >Applicable</span
                        >
                      </label>
                    </div>
                    <label class="tw:label" :class="{ 'tw:hidden': validFormFields.attachmentPointAmount }">
                      <span
                        v-show="!validFormFields.attachmentPointAmount"
                        class="tw:label-text-alt tw:text-xs tw:text-red-500"
                        >invalid amount</span
                      >
                    </label>
                  </div>
                </div>
                <div class="tw:flex tw:gap-6">
                  <div class="tw:w-1/2">
                    <label class="tw:label">
                      <span class="tw:label-text tw:text-xs tw:text-gray-500">Aggregate Limit</span>
                    </label>
                    <div class="tw:flex tw:flex-row tw:gap-2">
                      <input
                        type="text"
                        inputmode="numeric"
                        placeholder=""
                        v-model="aggregateLimitAmount"
                        class="tw:input tw:input-md tw:w-full tw:border tw:border-solid"
                        :class="{
                          'tw:border-red-500': !validFormFields.aggregateLimitAmount,
                          'tw:border-green-500':
                            structureFieldsChanged.aggregateLimitAmount && validFormFields.aggregateLimitAmount,
                        }"
                        :disabled="!form.aggregateLimitApplicable || isFormDisabled" />
                      <label class="tw:form-control tw:label">
                        <input
                          type="checkbox"
                          class="tw:toggle tw:border tw:border-solid tw:border-gray-400 tw:toggle-primary"
                          :class="{ 'tw:border-green-500': structureFieldsChanged.aggregateLimitApplicable }"
                          v-model="form.aggregateLimitApplicable"
                          :disabled="isFormDisabled"
                          :checked="form.aggregateLimitApplicable" />
                        <span
                          class="tw:text-center tw:align-top tw:text-xs"
                          :class="form.aggregateLimitApplicable && 'tw:text-primary'"
                          >Applicable</span
                        >
                      </label>
                    </div>
                    <label class="tw:label" :class="{ 'tw:hidden': validFormFields.aggregateLimitAmount }">
                      <span
                        v-show="!validFormFields.aggregateLimitAmount"
                        class="tw:label-text-alt tw:text-xs tw:text-red-500"
                        >invalid amount</span
                      >
                    </label>
                  </div>
                  <div class="tw:w-1/2">
                    <label class="tw:label">
                      <span class="tw:label-text tw:text-xs tw:text-gray-500">Occurrence Limit</span>
                    </label>
                    <div class="tw:flex tw:flex-row tw:gap-2">
                      <input
                        type="text"
                        inputmode="numeric"
                        placeholder=""
                        v-model="occurrenceLimitAmount"
                        class="tw:input tw:input-md tw:w-full tw:border tw:border-solid"
                        :class="{
                          'tw:border-red-500': !validFormFields.occurrenceLimitAmount,
                          'tw:border-green-500':
                            structureFieldsChanged.occurrenceLimitAmount && validFormFields.occurrenceLimitAmount,
                        }"
                        :disabled="!form.occurrenceLimitApplicable || isFormDisabled" />
                      <label class="tw:form-control tw:label">
                        <input
                          type="checkbox"
                          class="tw:toggle tw:border tw:border-solid tw:border-gray-400 tw:toggle-primary"
                          :class="{ 'tw:border-green-500': structureFieldsChanged.occurrenceLimitApplicable }"
                          v-model="form.occurrenceLimitApplicable"
                          :disabled="isFormDisabled"
                          :checked="form.occurrenceLimitApplicable" />
                        <span
                          class="tw:text-center tw:align-top tw:text-xs"
                          :class="form.occurrenceLimitApplicable && 'tw:text-primary'"
                          >Applicable</span
                        >
                      </label>
                    </div>
                    <label class="tw:label" :class="{ 'tw:hidden': validFormFields.occurrenceLimitAmount }">
                      <span
                        v-show="!validFormFields.occurrenceLimitAmount"
                        class="tw:label-text-alt tw:text-xs tw:text-red-500"
                        >invalid amount</span
                      >
                    </label>
                  </div>
                </div>
                <div class="tw:flex tw:gap-6">
                  <div class="tw:w-1/2">
                    <label class="tw:label">
                      <span class="tw:label-text tw:text-xs tw:text-gray-500">Total Layer Limit</span>
                    </label>
                    <input
                      type="text"
                      placeholder=""
                      v-model="quotaShareTotal"
                      class="tw:input tw:input-md tw:w-full tw:border tw:border-solid"
                      :class="{
                        'tw:border-red-500': !validFormFields.quotaShareTotal,
                        'tw:border-green-500':
                          structureFieldsChanged.quotaShareTotal && validFormFields.quotaShareTotal,
                      }"
                      :disabled="isFormDisabled" />
                    <label class="tw:label" :class="{ 'tw:hidden': validFormFields.quotaShareTotal }">
                      <span
                        v-show="!validFormFields.quotaShareTotal"
                        class="tw:label-text-alt tw:text-xs tw:text-red-500"
                        >invalid amount</span
                      >
                    </label>
                  </div>
                  <div class="tw:w-1/2">
                    <label class="tw:label">
                      <span class="tw:label-text tw:text-xs tw:text-gray-500">Retention</span>
                    </label>
                    <div class="tw:flex tw:flex-row tw:gap-2">
                      <input
                        type="text"
                        placeholder=""
                        v-model="retentionAmount"
                        class="tw:input tw:input-md tw:w-full tw:border tw:border-solid"
                        :class="{
                          'tw:border-red-500': !validFormFields.retentionAmount,
                          'tw:border-green-500':
                            structureFieldsChanged.retentionAmount && validFormFields.retentionAmount,
                        }"
                        :disabled="!form.retentionApplicable || isFormDisabled" />
                      <label class="tw:form-control tw:label">
                        <input
                          type="checkbox"
                          class="tw:toggle tw:border tw:border-solid tw:border-gray-400 tw:toggle-primary"
                          :class="{ 'tw:border-green-500': structureFieldsChanged.retentionApplicable }"
                          v-model="form.retentionApplicable"
                          :disabled="isFormDisabled"
                          :checked="form.retentionApplicable" />
                        <span
                          class="tw:text-center tw:align-top tw:text-xs"
                          :class="form.retentionApplicable && 'tw:text-primary'"
                          >Applicable</span
                        >
                      </label>
                    </div>
                    <label class="tw:label" :class="{ 'tw:hidden': validFormFields.retentionAmount }">
                      <span
                        v-show="!validFormFields.retentionAmount"
                        class="tw:label-text-alt tw:text-xs tw:text-red-500"
                        >invalid amount</span
                      >
                    </label>
                  </div>
                </div>
                <div class="tw:flex tw:gap-6" v-if="isPolicyLine || policySource?.AllowPremiumUpdate">
                  <div class="tw:w-1/2">
                    <label class="tw:label">
                      <span class="tw:label-text tw:text-xs tw:text-gray-500">Premium Total</span>
                    </label>
                    <input
                      type="text"
                      placeholder=""
                      v-model="premiumTotal"
                      class="tw:input tw:input-md tw:w-full tw:border tw:border-solid"
                      :class="{ 'tw:border-green-500': structureFieldsChanged.premiumTotal }"
                      :disabled="isFormDisabled" />
                  </div>
                  <div class="tw:w-1/2" :class="{ 'tw:hidden': isPolicyLine || !policySource?.AllowCarrierUpdate }">
                    <label class="tw:label">
                      <span class="tw:label-text tw:text-xs tw:text-gray-500">Carrier</span>
                    </label>
                    <AppSelect
                      v-model="form.carrierId"
                      :options="carrierOptions"
                      :class="`tw:h-12 tw:w-full ${structureFieldsChanged.carrierId ? 'tw:border-green-500' : ''}`"
                      :disabled="isFormDisabled" />
                  </div>
                </div>
                <div class="tw:flex tw:gap-6" :class="{ 'tw:hidden': isPolicyLine }">
                  <div class="tw:w-1/2">
                    <label class="tw:label">
                      <span class="tw:label-text tw:text-xs tw:text-gray-500">Lead Syndicate</span>
                    </label>
                    <AppSelect
                      v-model="form.leadSyndicateId"
                      :options="syndicateOptions"
                      :class="`tw:h-10 tw:w-full tw:rounded-sm ${structureFieldsChanged.leadSyndicateId ? 'tw:border-green-500' : ''}`"
                      menu-class="tw:w-[400px]"
                      :disabled="isFormDisabled" />
                  </div>
                </div>
                <div class="tw:flex tw:gap-6">
                  <div class="tw:w-1/2">
                    <div class="tw:w-2/3">
                      <label class="tw:label">
                        <span class="tw:label-text tw:text-xs tw:text-gray-500">Side A</span>
                      </label>
                      <AppSelect
                        v-model="form.sideAValue"
                        :options="sideAOptions.map((option) => ({ label: option, value: option }))"
                        :class="`tw:h-10 tw:w-full tw:rounded-sm ${structureFieldsChanged.sideAValue ? 'tw:border-green-500' : ''}`"
                        :disabled="isFormDisabled" />
                    </div>
                  </div>
                  <div class="tw:h-full tw:w-1/2" :class="{ 'tw:hidden': isPolicyLine }">
                    <label class="tw:label">
                      <span class="tw:label-text tw:text-xs tw:text-gray-500">Runoff</span>
                    </label>
                    <label class="tw:label tw:flex tw:justify-normal tw:gap-4">
                      <input
                        type="checkbox"
                        v-model="form.runoff"
                        class="tw:toggle tw:border tw:border-solid"
                        :class="{ 'tw:border-green-500': structureFieldsChanged.runoff }"
                        :disabled="isFormDisabled" />
                    </label>
                  </div>
                </div>
                <div class="tw:flex tw:gap-4"></div>
              </template>

              <!-- Servicing Roles Tab Content -->
              <template v-if="activeTab === 'servicing'">
                <div class="tw:min-h-[500px]">
                  <div v-if="servicingRolesLoading" class="tw:flex tw:h-64 tw:items-center tw:justify-center">
                    <span class="tw:loading tw:loading-lg tw:loading-spinner"></span>
                  </div>
                  <div v-else-if="servicingRoles.length === 0" class="tw:mt-8 tw:text-center tw:text-gray-500">
                    No servicing roles found for this policy.
                  </div>
                  <div v-else class="tw:space-y-4">
                    <div v-for="(role, index) in servicingRoles" :key="index" class="tw:relative tw:p-1">
                      <div class="tw:flex tw:items-center tw:gap-4">
                        <div class="tw:w-1/3 tw:text-sm tw:font-medium">
                          {{ role.role }}
                        </div>
                        <div class="tw:relative tw:w-2/3">
                          <!-- Show Combobox if employee key is valid, empty, or inactive -->
                          <div v-if="isValidEmployee(role.employeeInfo) || isNoLongerEmployed(role.employeeInfo)">
                          <div
                            class="tw:flex tw:w-full tw:items-center tw:gap-2">
                            <div class="tw:w-64 tw:shrink-0">
                              <Combobox
                                :model-value="role.employeeInfo.lookupCode"
                                @update:model-value="(value) => handleServicingRoleChange(index, value)"
                                :options="isCurrentlySelectedEmployeeInactive(role) ? getUserOptionsForRoleWithInactive(role) : getUserOptionsForRole(role)"
                                :disabled="isFormDisabled"
                                placeholder="Select a user..."
                                :input-class="`tw:input-sm tw:w-full ${
                                  isCurrentlySelectedEmployeeInactive(role) ? 'tw:pr-10 tw:border-red-500' : (isServicingRoleChanged(index) ? 'tw:pr-10 tw:border-green-500' : '')}`" />
                            </div>
                            <!-- Client Visible Toggle for non-empty roles -->
                            <div v-if="role.employeeInfo.lookupCode" class="tw:flex tw:w-20 tw:items-center">
                              <label class="tw:flex tw:items-center tw:gap-2">
                                <input
                                  type="checkbox"
                                  v-model="role.published"
                                  @change="handleClientViewableChange(index)"
                                  class="tw:toggle tw:border tw:border-solid tw:border-gray-400 tw:toggle-primary tw:toggle-sm"
                                  :disabled="isFormDisabled" />
                                <span class="tw:text-xs tw:text-gray-600">Client Visible</span>
                              </label>
                            </div>
                            <!-- Change indicator and undo button -->
                            <div v-if="isServicingRoleChanged(index)" class="tw:flex tw:shrink-0 tw:items-center">
                              <button
                                @click="revertServicingRole(index)"
                                class="tw:btn tw:text-gray-400 tw:transition-colors tw:btn-xs tw:hover:text-gray-600"
                                title="Undo change">
                                <Undo2 class="tw:h-4 tw:w-4" />
                              </button>
                            </div>
                          </div>
                            <div
                                v-if="isCurrentlySelectedEmployeeInactive(role)"
                                class="tw:mt-1 tw:flex tw:items-center tw:gap-1 tw:pl-1 tw:text-xs tw:text-red-600">
                              {{ createStatusText(role.employeeInfo) }}
                            </div>
                          </div>
                          <!-- Show disabled input if employee key doesn't match any user -->
                          <div v-else class="tw:w-full">
                            <div class="tw:w-64 tw:shrink-0">
                              <UIInput
                                :model-value="role.employeeInfo?.lookupCode"
                                disabled
                                class="tw:input-border tw:input-sm tw:w-full tw:border-amber-600" />
                            </div>
                            <div class="tw:mt-1 tw:flex tw:items-center tw:gap-1 tw:pl-1 tw:text-xs tw:text-amber-600">
                              {{ createStatusText(role.employeeInfo) }}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-if="role.description" class="tw:mt-2 tw:text-sm tw:text-gray-500">
                        {{ role.description }}
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </transition>
        </div>

        <div v-if="showHistory" class="tw:flex tw:min-w-full tw:flex-col tw:gap-4">
          <h1 class="tw:pb-3">{{ activeTab === 'structure' ? 'Override History' : 'Servicing Roles History' }}</h1>

          <!-- Structure History -->
          <div v-if="activeTab === 'structure'" class="tw:flex tw:flex-col tw:gap-1">
            <div v-if="overrideHistory.length === 0" class="tw:mt-8 tw:text-center tw:text-gray-500">
              No override history found for this policy.
            </div>
            <div
              v-else
              v-for="(rec, index) in overrideHistory"
              :key="index"
              class="collapse tw:collapse-plus tw:collapse tw:bg-base-200">
              <input type="radio" name="my-accordion-3" />
              <div class="tw:text-md tw:collapse-title tw:flex tw:flex-col tw:gap-1 tw:font-medium">
                <div>
                  {{ DateTime.fromISO(rec.createdAt || '', { zone: 'utc' }).toLocaleString(DateTime.DATETIME_SHORT) }}
                </div>
                <div>{{ rec.userEmail }}</div>
              </div>
              <div class="tw:collapse-content">
                <table class="tw:table tw:table-xs">
                  <thead class="tw:font-semibold tw:text-black">
                    <tr>
                      <th>Field</th>
                      <th>Original</th>
                      <th>Changed</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(diff, key) in changeDiff(rec.changeDiff)" :key="key">
                      <td>{{ policyFieldLabels[key]?.label || key }}</td>
                      <td class="tw:text-red-700">{{ formatDiffValue(key, diff.original) || '--' }}</td>
                      <td class="tw:text-green-700">{{ formatDiffValue(key, diff.changed) || '--' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Servicing Roles History -->
          <div v-if="activeTab === 'servicing'" class="tw:flex tw:flex-col tw:gap-1">
            <div v-if="servicingRolesAuditHistory.length === 0" class="tw:mt-8 tw:text-center tw:text-gray-500">
              No servicing roles history found for this policy.
            </div>
            <div
              v-else
              v-for="(rec, index) in servicingRolesAuditHistory"
              :key="index"
              class="collapse tw:collapse-plus tw:collapse tw:bg-base-200">
              <input type="radio" name="my-accordion-servicing" />
              <div class="tw:text-md tw:collapse-title tw:flex tw:flex-col tw:gap-1 tw:font-medium">
                <div>
                  {{ DateTime.fromISO(rec.createdAt || '', { zone: 'utc' }).toLocaleString(DateTime.DATETIME_SHORT) }}
                </div>
                <div>{{ rec.userEmail }}</div>
              </div>
              <div class="tw:collapse-content">
                <table class="tw:table tw:table-xs">
                  <thead class="tw:font-semibold tw:text-black">
                    <tr>
                      <th>Field</th>
                      <th>Original</th>
                      <th>Changed</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(diff, key) in parseServicingRolesChangeDiff(rec.changeDiff)" :key="key">
                      <td>{{ formatServicingRoleFieldLabel(key) }}</td>
                      <td class="tw:text-red-700">{{ getUserNameByLookupCode(diff.original) }}</td>
                      <td class="tw:text-green-700">{{ getUserNameByLookupCode(diff.changed) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </AppModal>
</template>

<style>
.collapse {
  visibility: visible !important;
}

/* fix issue with modal rendering not at full opacity on occasion */
.tw:modal.tw:modal-open {
  opacity: 1 !important;
}

/* Slide fade transition for tabs */
.slide-fade-enter-active {
  transition: all 0.1s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.1s ease-in;
}

.slide-fade-enter-from {
  transform: translateX(5px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(-5px);
  opacity: 0;
}
</style>
